import { useCallback, useEffect, useRef, useState } from "react";
import { fetchAnalytics, fetchInsights, regenerateInsights } from "@services/reactivation";

export const POLL_MS = 30000;
const LIVE = ["running"];

/**
 * Campaign numbers and the written insight for one clinic, as two
 * independent requests. Numbers never wait for the insight; the insight
 * never blanks the numbers. Later loads keep what is on screen (quiet) and
 * only expose `refreshing`, so the page can show a thin progress bar instead
 * of a blank.
 *
 * Every request is stamped, as in useReactivationCampaigns: a resolution
 * whose stamp is no longer current belongs to filters the user has left and
 * is thrown away rather than overwriting what is on screen.
 */
export function useReactivationAnalytics(clinicId, { campaigns = [] } = {}) {
  const [filters, setFiltersState] = useState({ campaignId: null, from: null, to: null });
  const [numbers, setNumbers] = useState(null);
  const [numbersLoading, setNumbersLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [numbersError, setNumbersError] = useState(null);
  const [insight, setInsight] = useState(null);
  const [insightLoading, setInsightLoading] = useState(false);
  const [insightError, setInsightError] = useState(null);
  const [regenerating, setRegenerating] = useState(false);
  const numbersReq = useRef(0);
  const insightReq = useRef(0);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const params = useCallback(() => ({ clinicId, ...filtersRef.current }), [clinicId]);

  const loadInsight = useCallback(async () => {
    const reqId = ++insightReq.current;
    setInsightLoading(true);
    setInsightError(null);
    try {
      const next = await fetchInsights(params());
      if (insightReq.current !== reqId) return;
      setInsight(next);
    } catch (e) {
      if (insightReq.current !== reqId) return;
      setInsightError(e.message);
    } finally {
      if (insightReq.current === reqId) setInsightLoading(false);
    }
  }, [params]);

  const refresh = useCallback(async ({ quiet = false } = {}) => {
    const reqId = ++numbersReq.current;
    if (quiet) setRefreshing(true); else setNumbersLoading(true);
    try {
      const next = await fetchAnalytics(params());
      if (numbersReq.current !== reqId) return;
      setNumbers(next);
      setNumbersError(null);
      loadInsight();
    } catch (e) {
      if (numbersReq.current !== reqId) return;
      setNumbersError(e.message);
    } finally {
      if (numbersReq.current === reqId) { setNumbersLoading(false); setRefreshing(false); }
    }
  }, [params, loadInsight]);

  const setFilters = useCallback((partial) => {
    setFiltersState((prev) => ({ ...prev, ...partial }));
  }, []);

  const regenerate = useCallback(async () => {
    const reqId = ++insightReq.current;
    setRegenerating(true);
    setInsightError(null);
    try {
      const next = await regenerateInsights(params());
      if (insightReq.current !== reqId) return;
      setInsight(next);
    } catch (e) {
      if (insightReq.current !== reqId) return;
      setInsightError(e.message);
    } finally {
      if (insightReq.current === reqId) setRegenerating(false);
    }
  }, [params]);

  // First load and clinic change: a blank slate and a non-quiet load.
  useEffect(() => {
    numbersReq.current += 1;
    insightReq.current += 1;
    setNumbers(null);
    setInsight(null);
    setNumbersError(null);
    setInsightError(null);
    refresh();
  }, [clinicId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Filter change: keep the content, reload quietly.
  const firstFilters = useRef(true);
  useEffect(() => {
    if (firstFilters.current) { firstFilters.current = false; return; }
    refresh({ quiet: true });
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!campaigns.some((c) => LIVE.includes(c.status))) return undefined;
    const id = setInterval(() => refresh({ quiet: true }), POLL_MS);
    return () => clearInterval(id);
  }, [campaigns, refresh]);

  return {
    filters, setFilters, numbers, numbersLoading, refreshing, numbersError, refresh,
    insight, insightLoading, insightError, retryInsight: loadInsight, regenerate, regenerating,
  };
}
